import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Badge, Box, Button, CircularProgress, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getLibraryConfig, getReviewQueue } from './apiLibrary';
import { BRAND, LIBRARY_DISCLAIMER } from './libraryConstants';
import LibrarySearch from './LibrarySearch';
import LibraryItemDetail from './LibraryItemDetail';
import LibraryItemForm from './LibraryItemForm';
import MyDrafts from './MyDrafts';
import ReviewQueue from './ReviewQueue';
import LibraryRoles from './LibraryRoles';
import LibraryHelp from './LibraryHelp';

const RANK = { viewer: 0, contributor: 1, approver: 2 };

// Filters, the search term, the open item and the sub-view live in the query
// string (?tab=library&view=review&type=PR&q=351&item=<id>) so views can be shared.
const readParams = (search) => {
    const p = new URLSearchParams(search);
    const list = (k) => (p.get(k) || '').split(',').map(s => s.trim()).filter(Boolean);
    return {
        view: p.get('view') || 'browse',
        type: list('type'),
        mfr: list('mfr'),
        q: p.get('q') || '',
        sort: p.get('sort') === '-publishedAt' ? '-publishedAt' : 'code',
        page: Math.max(1, parseInt(p.get('page'), 10) || 1),
        item: p.get('item') || ''
    };
};

const DeviceLibraryTab = ({ groupId }) => {
    const location = useLocation();
    const history = useHistory();
    const [config, setConfig] = useState(null);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [reviewCount, setReviewCount] = useState(0);
    const [formItem, setFormItem] = useState(undefined); // undefined = closed, null = new device
    const [help, setHelp] = useState(null); // null = closed, otherwise the section to open ('' = default for role)

    const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

    useEffect(() => {
        let cancelled = false;
        getLibraryConfig(groupId).then(res => {
            if (cancelled) return;
            if (res.error) setError(res.error === 'Not found' ? 'The Device Library is not available for this committee.' : res.error);
            else setConfig(res);
        });
        return () => { cancelled = true; };
    }, [groupId]);

    const rank = config ? RANK[config.role] : 0;

    useEffect(() => {
        if (rank < RANK.approver) return undefined;
        let cancelled = false;
        getReviewQueue(groupId).then(res => {
            if (!cancelled && !res.error) setReviewCount(res.items.length + (res.releases || []).length);
        });
        return () => { cancelled = true; };
    }, [groupId, rank, refreshKey]);

    const params = useMemo(() => readParams(location.search), [location.search]);

    const updateParams = useCallback((changes) => {
        const p = new URLSearchParams(location.search);
        p.set('tab', 'library');
        Object.entries(changes).forEach(([k, v]) => {
            const value = Array.isArray(v) ? v.join(',') : v;
            const isDefault = (k === 'page' && Number(value) === 1) || (k === 'sort' && value === 'code') || (k === 'view' && value === 'browse');
            if (value === '' || value === undefined || value === null || isDefault) p.delete(k);
            else p.set(k, value);
        });
        history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
    }, [history, location.pathname, location.search]);

    if (error) return <Alert severity="info">{error}</Alert>;
    if (!config) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

    const views = [
        { value: 'browse', label: 'Browse', show: true, tip: 'Search and download published devices' },
        { value: 'drafts', label: 'My drafts', show: rank >= RANK.contributor, tip: 'Your devices that are not published yet, including any returned with a note' },
        { value: 'review', label: 'Review queue', show: rank >= RANK.approver, badge: reviewCount, tip: 'Devices waiting for an approver' },
        { value: 'roles', label: 'Roles', show: config.isGroupAdmin, tip: 'Choose who can contribute and who can approve' }
    ].filter(v => v.show);
    const view = views.some(v => v.value === params.view) ? params.view : 'browse';

    const openItem = (id) => updateParams({ item: id });
    const addDevice = rank >= RANK.contributor ? () => setFormItem(null) : undefined;

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 1 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: BRAND.primary, mb: 1 }}>Device Library</Typography>
                    <Typography color="text.secondary">CAD footprints and reference files for devices used in substation design.</Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button variant="outlined" startIcon={<HelpOutlineIcon />} onClick={() => setHelp('')}>How it works</Button>
                    {addDevice && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={addDevice}
                            sx={{ bgcolor: BRAND.action, '&:hover': { bgcolor: BRAND.actionHover } }}>
                            Add a device
                        </Button>
                    )}
                </Stack>
            </Stack>
            {config.roleOverride && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Testing mode: ROLE_LOCAL={config.roleOverride} in the API's .env is showing the library as a {config.role}. Clear it to get your real role back.
                </Alert>
            )}
            <Alert severity="info" sx={{ my: 2 }}>{LIBRARY_DISCLAIMER}</Alert>

            {views.length > 1 && (
                <Tabs value={view} onChange={(e, v) => updateParams({ view: v, item: '' })} variant="scrollable" allowScrollButtonsMobile
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
                    {views.map(v => (
                        <Tab key={v.value} value={v.value} sx={v.badge ? { pr: 3 } : undefined} label={
                            <Tooltip title={v.tip}>
                                {v.badge ? (
                                    <Badge color="warning" badgeContent={v.badge} sx={{ '& .MuiBadge-badge': { right: -12 } }}>{v.label}</Badge>
                                ) : <span>{v.label}</span>}
                            </Tooltip>
                        } />
                    ))}
                </Tabs>
            )}

            {view === 'browse' && (
                <LibrarySearch
                    groupId={groupId}
                    config={config}
                    filters={params}
                    refreshKey={refreshKey}
                    onFiltersChange={updateParams}
                    onOpenItem={openItem}
                    onAddDevice={addDevice}
                />
            )}
            {view === 'drafts' && <MyDrafts groupId={groupId} refreshKey={refreshKey} onOpenItem={openItem} />}
            {view === 'review' && (
                <ReviewQueue groupId={groupId} config={config} refreshKey={refreshKey}
                    onOpenItem={openItem} onEdit={setFormItem} onChanged={refresh} />
            )}
            {view === 'roles' && <LibraryRoles groupId={groupId} />}

            <LibraryItemDetail
                groupId={groupId}
                itemId={params.item}
                config={config}
                refreshKey={refreshKey}
                onClose={() => updateParams({ item: '' })}
                onOpenItem={openItem}
                onEdit={setFormItem}
                onChanged={refresh}
            />

            {formItem !== undefined && (
                <LibraryItemForm
                    groupId={groupId}
                    config={config}
                    item={formItem}
                    onClose={() => setFormItem(undefined)}
                    onSaved={refresh}
                    onOpenExisting={(id) => { setFormItem(undefined); openItem(id); }}
                    onHelp={() => setHelp('contribute')}
                />
            )}

            <LibraryHelp open={help !== null} onClose={() => setHelp(null)} role={config.role} initialSection={help || undefined} />
        </Box>
    );
};

export default DeviceLibraryTab;
